// import '@/globals.css'
import { Button } from './ui/button'
import { Card } from './ui/card'
import { BadgeDollarSign } from 'lucide-react'

import { PropsWithChildren } from 'react'

type Props = PropsWithChildren<{
  viewportRef?: React.RefObject<HTMLElement>
}>
export default function PageLayout({ viewportRef }: Props) {
  return (
    <div className="max-w-[100vw]">
      <main ref={viewportRef} className="w-full max-w-full h-fit relative">
        <div className="flex flex-col justify-center items-center bg-gradient-to-b from-emerald-500 to-emerald-200 text-emerald-50 h-[50vh]">
          <div className="flex flex-col justify-center items-center gap-3 max-w-sm">
            <h1 className="font-display text-white text-6xl leading-tight drop-shadow-xl">
              Dumblift
            </h1>
            <span className="leading-tight text-medium font-display text-gradient-emerald drop-shadow-none text-center text-pretty">
              Built for experienced weight lifters looking for a simple and
              quick way to track their progress. No questionnaires. No rigid
              A.I. coaching plans. Just a flexible and focused tool that does
              what you want.
            </span>
            <span className="drop-shadow-xl text-xl font-medium">
              You're smart. So we're dumb.
            </span>
            <Button className="mt-5" asChild size="xl">
              <a href="/app/">Get Started</a>
            </Button>
          </div>
        </div>
        <div className="w-full flex flex-col relative bg-gradient-to-b from-stone-900 to-emerald-900">
          <h2 className="w-full text-center px-10 py-6 font-display text-3xl text-emerald-100">
            Features
          </h2>
          <section className="w-full min-h-[50vh] relative flex flex-col md:flex-row">
            <div className="w-full h-full flex flex-col justify-center items-center pb-7 px-4">
              <img
                style={{
                  minWidth: 340,
                  minHeight: 570,
                  // backgroundImage: 'url(data:image/webp;base64,UklGRhwBAABXRUJQVlA4IBABAABQBgCdASoRAB4APpE8mEiloyIhMBgIALgSCWYAwfsCJA5dWyXQSGx3aym5TRyHvb27vtvtx6eRbdakAAD+/BgIktUee6W8Ic7ylItzfJ8f6TyqpyXl31B3/EjSwaDKIwM8pl7idyOx21CNPOua/adP3cv2Csf6gMoGdv0KQoPPyDxC8c673ceh+V5b0lPn5N0xSU/Aq4Sq17cB6q6/6Wx1QNRo0Hc8Yk2li3NVEFXZ0aytE9EzLJxWETt6/Ad6mJ7pRRb9bMDykG5nic0v/1j8r9m56mRH+fEQ9O0wXqoanQf1Z7YanLHX/O4z0dmVo++LZLRFybr2thfuy/an6BXFingRdp+oXTAI/qF0aAAAAA==)',
                  // backgroundRepeat: 'no-repeat',
                  // backgroundSize: '100% 100%',
                  // backgroundPosition: 'top left',
                  // color: 'transparent',
                  // filter: 'blur(6px)',
                }}
                loading="eager"
                src="/screen_stamp.webp"
                alt="screenshot of app recording your sets."
                className="max-h-full max-w-[20em] skew-y-3 rotate-0 rounded-md shadow-2xl shadow-emerald-900"
              />
            </div>
            <div className="z-20 p-10 -mt-20 flex flex-col justify-center items-center">
              <Card className="p-4">
                <h3 className="text-lg tracking-tight mb-2 whitespace-nowrap">
                  Quick and Easy Tracking
                </h3>
                <p className="font-sans tracking-tight leading-tight">
                  An interface modelled after spreadsheets&mdash;spreadsheets
                  with weight training focused shortcuts that aren't awful to
                  use on your phone.
                </p>
              </Card>
            </div>
          </section>

          <section className="w-full min-h-[50vh] relative flex flex-col md:flex-row-reverse">
            <div className="w-full h-full flex flex-col justify-center items-center pb-7 px-4">
              <img
                style={{
                  minWidth: 340,
                  minHeight: 570,
                }}
                src="/screen_charts.webp"
                alt="screenshot of app displaying historical charts."
                className="max-h-full max-w-[20em] -skew-y-3 rotate-0 rounded-md shadow-2xl shadow-emerald-800"
              />
            </div>
            <div className="z-20 p-10 -mt-20 flex flex-col justify-center items-center">
              <Card className="p-4">
                <h3 className="text-lg tracking-tight mb-2 whitespace-nowrap">
                  Progress Charts
                </h3>
                <p className="font-sans tracking-tight leading-tight">
                  Keep your eye on progress with handsome graphs for different
                  metrics like "estimated one-rep maximum" and "volume". Focus
                  on your goals with simple sorting and filtering tools.
                </p>
              </Card>
            </div>
          </section>

          <section className="w-full min-h-[50vh] relative flex flex-col md:flex-row">
            <div className="w-full h-full flex flex-col justify-center items-center pb-7 px-4">
              <img
                style={{
                  minWidth: 340,
                  minHeight: 570,
                }}
                src="/screen_records.webp"
                alt="screenshot of app displaying historical records."
                className="max-h-full max-w-[20em] skew-y-3 rotate-0 rounded-md shadow-2xl shadow-emerald-700"
              />
            </div>
            <div className="z-20 p-10 -mt-20 flex flex-col justify-center items-center">
              <Card className="p-4">
                <h3 className="text-lg tracking-tight mb-2 whitespace-nowrap">
                  Exercise Records and Customization
                </h3>
                <p className="font-sans tracking-tight leading-tight">
                  View your PRs in one place and tweak exercises to your needs.
                </p>
              </Card>
            </div>
          </section>

          <div className="w-full max-w-full overflow-auto snap-both p-6 flex flex-col md:flex-row md:touch-pan-x gap-3 justify-start items-stretch">
            <Card className="p-4 md:pb-6">
              <h3 className="text-lg mb-2 tracking-tight whitespace-nowrap">
                Offline First
              </h3>
              <p className="font-sans tracking-tightest leading-tight font-sm">
                Install the app and use without a connection, so recording sets
                in your dingy basement gym is not a problem.
              </p>
            </Card>

            <Card className="p-4 relative md:pb-6">
              <span className="absolute px-2 uppercase py-1 text-xs rounded-lg bg-emerald-500 text-emerald-50 top-1 -right-3 md:top-initial md:-bottom-3 md:right-1">
                paid
                <BadgeDollarSign />
              </span>
              <h3 className="text-lg mb-2 tracking-tight whitespace-nowrap">
                Sync Across Devices
              </h3>
              <p className="font-sans tracking-tightest leading-tight font-sm">
                Sync your history across your devices in real-time so you can
                create programs and view those big beautiful charts from the
                comfort of your laptop.
              </p>
            </Card>

            <Card className="p-4 relative md:pb-6">
              <span className="absolute px-2 uppercase py-1 text-xs rounded-lg bg-emerald-500 text-emerald-50 top-1 -right-3 md:top-initial md:-bottom-3 md:right-1">
                paid
                <BadgeDollarSign />
              </span>
              <h3 className="text-lg mb-2 tracking-tight whitespace-nowrap">
                Multiple Profiles
              </h3>
              <p className="font-sans tracking-tightest leading-tight font-sm">
                Keep seperate profiles for multiple users.
              </p>
            </Card>

            <Card className="p-4 relative md:pb-6">
              <h3 className="text-lg mb-2 tracking-tight whitespace-nowrap">
                Own Your Data
              </h3>
              <p className="font-sans tracking-tightest leading-tight font-sm">
                Export you history to a raw SQLite database at any time.
              </p>
            </Card>
          </div>
        </div>
        <div className="w-full flex flex-col justify-center items-center py-20 px-10 bg-gradient-to-b from-emerald-300 to-emerald-400 gap-20 sm:flex-row sm:gap-32">
          <Testimonial
            imageUrl="/donny_smith.webp"
            name="Quiz Kid Donny Smith"
            quote="I used to be smart, but now I'm just dumb."
          />
          <Testimonial
            imageUrl="/noonian_soong.webp"
            name="Dr. Noonian Soong"
            quote="I wish I had built it. But I'm too dumb."
          />
        </div>
      </main>
      <footer className="flex flex-col justify-center items-center bg-emerald-900 p-20">
        <p className="text-lg text-emerald-200">🫀 &gt; 🧠</p>
        <p className="text-lg text-emerald-200">
          <span className="tabular-nums">
            &copy; {new Date().getFullYear()}
          </span>
          <span className="font-light">&nbsp;Joey Thies</span>
        </p>
      </footer>
    </div>
  )
}

function Testimonial(props: { quote: string; name: string; imageUrl: string }) {
  return (
    <div className="flex flex-col justify-center items-center gap-2">
      <img
        src={props.imageUrl}
        alt={props.name}
        className="w-32 h-32 rounded-full drop-shadow-xl"
      />
      <blockquote className="max-w-[12em]">
        <p className="italic font-medium text-stone-50 text-lg leading-tight">
          {props.quote}
        </p>
        <footer className="text-emerald-800 font-light text-sm">
          &mdash;{props.name}
        </footer>
      </blockquote>
    </div>
  )
}
